import React from "react";
import { useParams, useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, Calendar, Package, Truck, FileText } from "lucide-react";
import { format } from "date-fns";
import { getAuthToken, getTenantId } from "@/lib/auth";
import type { Order } from "../../../shared/schema";

export default function OrderDetails() {
  const { id } = useParams<{ id: string }>();
  const [, navigate] = useLocation();

  const { data: order, isLoading, error } = useQuery<Order>({
    queryKey: ["/api/orders", id],
    queryFn: async () => {
      const token = getAuthToken();
      const tenantId = getTenantId();
      const response = await fetch(`/api/orders/${id}?expand=all`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Tenant-Id": tenantId || "",
        },
      });
      if (!response.ok) throw new Error("Failed to fetch order");
      return response.json();
    },
    enabled: !!id,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <FileText className="h-8 w-8 animate-spin mx-auto mb-4 text-[hsl(187,100%,42%)]" />
          <p className="text-muted-foreground">Loading order details...</p>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <FileText className="h-8 w-8 mx-auto mb-4 text-red-500" />
          <p className="text-muted-foreground">Failed to load order</p>
          <Button
            onClick={() => navigate("/orders")}
            className="mt-4"
            variant="outline"
          >
            Back to Orders
          </Button>
        </div>
      </div>
    );
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending":
        return "bg-yellow-500/20 text-yellow-400";
      case "confirmed":
        return "bg-blue-500/20 text-blue-400";
      case "in_transit":
        return "bg-cyan-500/20 text-cyan-400";
      case "delivered":
        return "bg-green-500/20 text-green-400";
      case "cancelled":
        return "bg-red-500/20 text-red-400";
      default:
        return "bg-gray-500/20 text-gray-400";
    }
  };

  return (
    <div className="space-y-6">
      {/* Back Button and Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/orders")}
          data-testid="button-back-to-orders"
        >
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-[hsl(187,100%,42%)]">
            {order.orderNumber}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Order Details
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Order Information */}
          <Card className="bg-[hsl(215,28%,10%)] border border-[hsl(215,28%,18%)]">
            <CardHeader>
              <CardTitle className="text-[hsl(187,100%,42%)]">
                Order Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Status</p>
                  <Badge className={getStatusColor(order.status)}>
                    {order.status.replace("_", " ").toUpperCase()}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">
                    Reference Number
                  </p>
                  <p className="text-white font-mono">
                    {order.referenceNumber || "N/A"}
                  </p>
                </div>
              </div>

              <div>
                <p className="text-xs text-muted-foreground mb-1">
                  Description
                </p>
                <p className="text-white">
                  {order.specialInstructions || order.product || "No description"}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Product & Quantity */}
          <Card className="bg-[hsl(215,28%,10%)] border border-[hsl(215,28%,18%)]">
            <CardHeader>
              <CardTitle className="text-[hsl(187,100%,42%)] flex items-center gap-2">
                <Package className="w-5 h-5" />
                Product & Quantity
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Product</p>
                  <p className="text-white font-medium">
                    {order.product || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">
                    Quantity
                  </p>
                  <p className="text-white font-medium">
                    {order.quantity} {order.unit || ""}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Timeline */}
          <Card className="bg-[hsl(215,28%,10%)] border border-[hsl(215,28%,18%)]">
            <CardHeader>
              <CardTitle className="text-[hsl(187,100%,42%)] flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Timeline
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">
                    Created Date
                  </p>
                  <p className="text-white">
                    {order.createdAt
                      ? format(new Date(order.createdAt), "MMM dd, yyyy HH:mm")
                      : "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">
                    Requested Delivery Date
                  </p>
                  <p className="text-white">
                    {order.requestedDeliveryDate
                      ? format(
                          new Date(order.requestedDeliveryDate),
                          "MMM dd, yyyy"
                        )
                      : "N/A"}
                  </p>
                </div>
              </div>
              {order.updatedAt && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">
                    Last Updated
                  </p>
                  <p className="text-white">
                    {format(new Date(order.updatedAt), "MMM dd, yyyy HH:mm")}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Client Information */}
          <Card className="bg-[hsl(215,28%,10%)] border border-[hsl(215,28%,18%)]">
            <CardHeader>
              <CardTitle className="text-[hsl(187,100%,42%)] text-base">
                Client
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-white font-medium">
                {(order as any).customerName || "N/A"}
              </p>
            </CardContent>
          </Card>

          {/* Collection Point */}
          <Card className="bg-[hsl(215,28%,10%)] border border-[hsl(215,28%,18%)]">
            <CardHeader>
              <CardTitle className="text-[hsl(187,100%,42%)] text-base flex items-center gap-2">
                <Truck className="w-4 h-4" />
                Collection Point
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-white font-medium">
                {(order as any).collectionPoint || "N/A"}
              </p>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="space-y-2">
            <Button
              className="w-full bg-[hsl(24.6,95%,53%)] hover:bg-[hsl(24.6,90%,48%)]"
              data-testid="button-edit-order"
            >
              Edit Order
            </Button>
            <Button
              variant="outline"
              className="w-full border-[hsl(215,28%,25%)]"
              onClick={() => navigate("/orders")}
              data-testid="button-back-to-list"
            >
              Back to List
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
